/**
 * Device Detection Utilities
 * Detects device type and capabilities for routing decisions
 */

export const DeviceDetection = {
  /**
   * Check if user is on mobile device
   * @returns {boolean}
   */
  isMobile() {
    // Check user agent for mobile indicators
    // eslint-disable-next-line no-undef
    const userAgent = navigator.userAgent || navigator.vendor || (typeof opera !== 'undefined' ? opera : '');
    
    // Mobile user agent patterns
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    
    // Screen size check (mobile typically < 768px width)
    const isSmallScreen = window.innerWidth < 768;
    
    // Touch capability check
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    return mobileRegex.test(userAgent) || (isSmallScreen && isTouchDevice);
  },

  /**
   * Check if user is on tablet
   * @returns {boolean}
   */
  isTablet() {
    // eslint-disable-next-line no-undef
    const userAgent = navigator.userAgent || navigator.vendor || (typeof opera !== 'undefined' ? opera : '');
    const tabletRegex = /iPad|Android(?!.*Mobile)/i;
    const isTabletScreen = window.innerWidth >= 768 && window.innerWidth <= 1024;
    
    return tabletRegex.test(userAgent) || isTabletScreen;
  },

  /**
   * Check if user is on desktop/computer
   * @returns {boolean}
   */
  isDesktop() {
    return !this.isMobile() && !this.isTablet();
  },

  /**
   * Get device type string
   * @returns {'mobile' | 'tablet' | 'desktop'}
   */
  getDeviceType() {
    if (this.isMobile()) return 'mobile';
    if (this.isTablet()) return 'tablet';
    return 'desktop';
  },

  /**
   * Check if device has touch capability
   * @returns {boolean}
   */
  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  },

  /**
   * Get screen size category
   * @returns {'small' | 'medium' | 'large' | 'xlarge'}
   */
  getScreenSize() {
    const width = window.innerWidth;
    if (width < 640) return 'small';    // Mobile
    if (width < 768) return 'medium';   // Large mobile
    if (width < 1024) return 'large';   // Tablet
    return 'xlarge';                    // Desktop
  },

  /**
   * Check if user prefers mobile interface
   * Considers device type, screen size, and user preferences
   * IMPORTANT: Only treat as mobile if it's ACTUALLY a mobile device (user agent)
   * Don't rely on touch capability alone - many desktops have touchscreens
   * @returns {boolean}
   */
  prefersMobileInterface() {
    // Check if user has manually set preference
    const savedPreference = localStorage.getItem('odv-interface-preference');
    if (savedPreference === 'mobile') return true;
    if (savedPreference === 'desktop') return false;

    // Auto-detect based on device
    // ONLY treat as mobile if user agent indicates actual mobile device
    // Don't use touch capability alone - desktop monitors often have touchscreens
    // eslint-disable-next-line no-undef
    const userAgent = navigator.userAgent || navigator.vendor || (typeof opera !== 'undefined' ? opera : '');
    const actualMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

    return actualMobileUA;
  },

  /**
   * Get appropriate route based on user role and device
   * @param {Object} options - Options object
   * @param {Object} options.valet - Valet profile object (if exists)
   * @param {Object} options.user - Auth user object (optional)
   * @returns {string} - Route path
   */
  getAppropriateRoute({ valet, user } = {}) {
    const deviceType = this.getDeviceType();
    const prefersMobile = this.prefersMobileInterface();

    // If user has valet profile
    if (valet) {
      // Check both onboarding_completed AND contract_signed
      const isFullyOnboarded = valet.onboarding_completed && valet.contract_signed;

      if (isFullyOnboarded) {
        // Completed valet - route based on device preference
        if (prefersMobile || deviceType === 'mobile') {
          console.log(`[DeviceDetection] Valet → Mobile interface (/valet)`);
          return '/valet'; // Mobile-optimized valet interface
        } else {
          console.log(`[DeviceDetection] Valet → Desktop interface (/operations/assignments)`);
          return '/operations/assignments'; // Desktop dashboard
        }
      } else {
        // Needs onboarding - always use responsive onboarding flow
        console.log(`[DeviceDetection] Valet → Onboarding (/onboard)`, {
          onboarding_completed: valet.onboarding_completed,
          contract_signed: valet.contract_signed
        });
        return '/onboard';
      }
    }

    // Admin/KOT users (no valet profile)
    if (prefersMobile || deviceType === 'mobile') {
      // Mobile admin interface
      console.log(`[DeviceDetection] Admin → Mobile interface (/operations/assignments?mobile=true)`);
      return '/operations/assignments?mobile=true';
    } else {
      // Desktop admin interface
      console.log(`[DeviceDetection] Admin → Desktop interface (/operations/assignments)`);
      return '/operations/assignments';
    }
  },

  /**
   * Log device info for debugging
   */
  logDeviceInfo() {
    console.log('🔍 Device Detection:', {
      userAgent: navigator.userAgent,
      deviceType: this.getDeviceType(),
      screenSize: this.getScreenSize(),
      screenDimensions: `${window.innerWidth}x${window.innerHeight}`,
      isTouchDevice: this.isTouchDevice(),
      prefersMobileInterface: this.prefersMobileInterface(),
      savedPreference: localStorage.getItem('odv-interface-preference')
    });
  },

  /**
   * Set user interface preference
   * @param {'mobile' | 'desktop' | 'auto'} preference
   */
  setInterfacePreference(preference) {
    if (preference === 'auto') {
      localStorage.removeItem('odv-interface-preference');
    } else {
      localStorage.setItem('odv-interface-preference', preference);
    }
    console.log(`🎯 Interface preference set to: ${preference}`);
  }
};

export default DeviceDetection;
